/**
 * Respuestas rápidas de un chat: botones que el FRONT dibuja según el estado
 * de la conversación (no texto del modelo). Antes estaban inline en el drawer
 * del asistente (`SUGGESTIONS.map`) y en TA Metrics; el asistente de MFA del
 * Vault (28/09) es el primero que las usa como máquina de estados.
 *
 *   <QuickReplies options={[{ id: 'yes', label: 'Sí' }]} onPick={(id) => …} />
 */
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface QuickReplyOption {
  id: string;
  label: string;
  icon?: ReactNode;
  /** `primary` resalta la opción esperada (p. ej. "Sí, coincide"). */
  tone?: 'default' | 'primary' | 'danger';
}

export function QuickReplies({ options, onPick, disabled, className, tour }: {
  options: QuickReplyOption[];
  onPick: (id: string) => void;
  disabled?: boolean;
  className?: string;
  /** Prefijo de `data-tour` para el workshop guiado: `${tour}-${id}`. */
  tour?: string;
}) {
  if (!options.length) return null;
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {options.map((o) => (
        <Button
          key={o.id}
          type="button"
          size="sm"
          variant={o.tone === 'primary' ? 'default' : 'outline'}
          className={cn('h-auto rounded-full px-3 py-1.5 text-left text-sm whitespace-normal', o.tone === 'danger' && 'border-destructive/40 text-destructive hover:bg-destructive/10')}
          disabled={disabled}
          onClick={() => onPick(o.id)}
          data-tour={tour ? `${tour}-${o.id}` : undefined}
        >
          {o.icon}
          {o.label}
        </Button>
      ))}
    </div>
  );
}
